import React, { useCallback } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { Icon, COLORS } from '@livecommerce/design-system';
import ProductSwitcher from 'components-v3/ProductSwitcher/index.jsx';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import {
  Container,
  ProductHeaderCollapsed,
  ProductSwitcherContainer,
  logoStyles,
  StyledWorkspaceLogoImg,
} from './styles.js';
import useOrganizations from 'hooks/useOrganizations.js';
import useClickOutside from 'hooks/useClickOutside.js';
import ExpandedProductHeader from 'components-v3/ProductHeader/ExpandedProductHeader.jsx';
import useOrgGeneralSettings from 'pages/GeneralSettings/useOrgGeneralSettings.js';
import { CLIENT_PRODUCT, ON_DEMAND_PRODUCT, ONE_TO_MANY_PRODUCT } from 'constants/products.js';
import useCurrentUserAccess from 'hooks/useCurrentUserAccess.js';
import ProductStats from './ProductStats.jsx';
import { styled } from '@linaria/react';
import ProductUpdatesItem from './ProductUpdatesItem.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import AIChatBotHeaderItem from './AIChatBotHeaderItem.jsx';
import SalesforceChatHeaderItem from './SalesforceChatHeaderItem.jsx';
import HelpItem from './HelpItem.jsx';

const PRODUCT_MENU_ID = 'product-menu';

const Spacer = styled.div`
  flex: 1;
`;

const HeaderItems = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const ProductHeader = ({ setIsProductHeaderExpanded, isProductHeaderExpanded, forwardedRef, ...props }) => {
  const { data: organizations } = useOrganizations();
  const { data: { clients } = {} } = useCurrentUserAccess();
  const { selectedProduct, orgId, clientId } = useProductDataOutsideRoutes();
  const { isLoading, data: { workspaceLogo: { url: workspaceLogoUrl } = {} } = {} } = useOrgGeneralSettings();
  const { features: { productUpdatesListEnabled, aiChatBotEnabled, pendoSupportEnabled, salesforceChatEnabled } } = useFeatureContext();
  const { canHaveEnterpriseChat } = useNavigationFrameContext();

  // Only present product updates for 'One-to-many' and 'On-demand' products for now
  const shouldShowProductUpdates = productUpdatesListEnabled
    && [ONE_TO_MANY_PRODUCT, ON_DEMAND_PRODUCT].includes(selectedProduct);
  const shouldShowAiChatBot = aiChatBotEnabled;

  const handleProductMenuOpen = useCallback((trigger) => {
    setIsProductHeaderExpanded((prevValue) => {
      trackingService.track({
        eventType: `product-menu:${prevValue ? 'close' : 'open'}`,
        trigger,
      });

      return !prevValue;
    });
  }, [setIsProductHeaderExpanded]);

  const { ref } = useClickOutside(() => {
    if (!isProductHeaderExpanded) return;
    handleProductMenuOpen('click-outside');
  });

  const handleProductSelect = useCallback(() => {
    handleProductMenuOpen('product-switcher');
  }, [handleProductMenuOpen]);

  return (
    <Container {...props} ref={forwardedRef}>
      <div ref={ref}>
        <ProductHeaderCollapsed>
          <m.div layout className={logoStyles}>
            {workspaceLogoUrl && !isLoading && (
              <StyledWorkspaceLogoImg
                src={workspaceLogoUrl}
                alt="Bam Hub logo"
              />
            )}
            {!workspaceLogoUrl && !isLoading && (
              <Icon
                name="logoHorizontal"
                color={COLORS.white}
                height={14}
                width={56}
                data-test-id="sidebarBambuserLogo"
              />
            )}
          </m.div>
          <ProductSwitcherContainer layout>
            {selectedProduct && (
              <ProductSwitcher
                aria-controls={PRODUCT_MENU_ID}
                isSelected={isProductHeaderExpanded}
                onSelect={handleProductSelect}
                product={selectedProduct}
                customSubtitle={
                  selectedProduct === CLIENT_PRODUCT ?
                    clients?.[clientId]?.title :
                    organizations?.find((org) => org.id === orgId)?.title
                }
              />
            )}
          </ProductSwitcherContainer>
          <Spacer />
          <HeaderItems>
            <ProductStats />
            {shouldShowAiChatBot && <AIChatBotHeaderItem />}
            {salesforceChatEnabled && canHaveEnterpriseChat && <SalesforceChatHeaderItem />}
            {shouldShowProductUpdates && !pendoSupportEnabled && <ProductUpdatesItem />}
            {pendoSupportEnabled && <HelpItem />}
          </HeaderItems>
        </ProductHeaderCollapsed>
        <AnimatePresence>
          {isProductHeaderExpanded && (
            <ExpandedProductHeader
              layout
              id={PRODUCT_MENU_ID}
              aria-expanded={isProductHeaderExpanded}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              organizations={organizations}
              handleProductMenuOpen={handleProductMenuOpen}
            />
          )}
        </AnimatePresence>
      </div>
    </Container>
  );
};

ProductHeader.propTypes = {};

export default React.forwardRef((props, ref) => <ProductHeader {...props} forwardedRef={ref} />);
